import { Injectable } from '@angular/core'
import { LogAppErrorCommand } from '@app/shared/commands/log-app-error.command'
import { AppErrorLevel, AppErrorPayload } from '@app/shared/data-types/app-error'
import { CurrentUser } from '@shared/services/current-user'

export interface ErrorSenderVideoContext {
  record_uuid: string
  video_sources?: string[]
  has_720p?: boolean
  error_code?: number
  is_fallback_attempt?: boolean
  fallback_action?: string
  upload_state?: string
  is_bot?: boolean
  source_check?: {
    status?: number
    content_type?: string
    cors?: string
    error?: string
  }
  player?: {
    currentSrc?: string
    paused?: boolean
    currentTime?: number
    duration?: number
    bufferedPercent?: number
    readyState?: number
    networkState?: number
    videoWidth?: number
    videoHeight?: number
  }
}

export interface ErrorSenderHttpContext {
  status: number
  method: string
  url: string
  response_body?: string
}

export interface ErrorSenderOptions {
  title: string
  error_class: string
  level?: AppErrorLevel
  error_message?: string
  error_callstack?: string
  video?: ErrorSenderVideoContext
  http?: ErrorSenderHttpContext
  skipDedup?: boolean
}

@Injectable({ providedIn: 'root' })
export class ErrorSenderService {
  private dedupeMap = new Map<string, number>()
  private globalCount = 0
  private globalWindowStart = 0

  private readonly DEDUP_COOLDOWN_MS = 60_000
  private readonly GLOBAL_LIMIT = 10
  private readonly GLOBAL_WINDOW_MS = 60_000

  constructor(
    private logAppErrorCommand: LogAppErrorCommand,
    private currentUser: CurrentUser
  ) {}

  sendError(options: ErrorSenderOptions): void {
    const now = Date.now()

    if (!options.skipDedup) {
      const dedupeKey = `${options.error_class}:${options.title}`
      const lastSent = this.dedupeMap.get(dedupeKey)
      if (lastSent !== undefined && now - lastSent! < this.DEDUP_COOLDOWN_MS) {
        return
      }
      this.dedupeMap.set(dedupeKey, now)
    }

    if (now - this.globalWindowStart > this.GLOBAL_WINDOW_MS) {
      this.globalCount = 0
      this.globalWindowStart = now
    }
    if (this.globalCount >= this.GLOBAL_LIMIT) {
      return
    }

    this.globalCount++

    const errorClass = `Frontend.${options.error_class}`
    const payload: AppErrorPayload = {
      title: `[${errorClass}] ${options.title}`,
      error_class: errorClass,
      level: options.level || 'error',
      error_message: options.error_message,
      error_callstack: options.error_callstack,
      data: {
        client: {
          user_agent: navigator.userAgent,
          current_url: window.location.href,
          parent_url: window !== window.parent ? document.referrer || undefined : undefined,
          referrer: document.referrer || null,
          user_id: this.currentUser?.id || null,
          screen: `${screen.width}x${screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          device_pixel_ratio: window.devicePixelRatio,
          platform: navigator.platform,
          language: navigator.language,
          hardware_concurrency: navigator.hardwareConcurrency || null,
          touch: navigator.maxTouchPoints > 0 ? 'touchscreen' : 'desktop',
          visibility_state: document.visibilityState,
          connection: (navigator as any).connection
            ? {
                type: (navigator as any).connection.effectiveType,
                downlink: (navigator as any).connection.downlink,
                rtt: (navigator as any).connection.rtt,
              }
            : null,
        },
        ...(options.video ? { video: this.buildVideoData(options.video!) } : {}),
        ...(options.http ? { http: options.http } : {}),
      },
    }

    this.logAppErrorCommand.call(payload)
  }

  /**
   * VideoJS MediaError codes (W3C spec + encrypted extension).
   * @see https://docs.videojs.com/mediaerror
   */
  private getVideoJSErrorCodeLabel(code: number): string {
    const labels: Record<number, string> = {
      0: 'MEDIA_ERR_CUSTOM — custom error',
      1: 'MEDIA_ERR_ABORTED — playback aborted by the user',
      2: 'MEDIA_ERR_NETWORK — network error during download',
      3: 'MEDIA_ERR_DECODE — error during decoding',
      4: 'MEDIA_ERR_SRC_NOT_SUPPORTED — source format not supported',
      5: 'MEDIA_ERR_ENCRYPTED — encrypted media error',
    }
    return labels[code] || `Unknown VideoJS error code (${code})`
  }

  private buildVideoData(video: ErrorSenderVideoContext): Record<string, unknown> {
    const readyStateLabels: Record<number, string> = {
      0: 'HAVE_NOTHING — no video data available',
      1: 'HAVE_METADATA — duration and dimensions loaded',
      2: 'HAVE_CURRENT_DATA — data for current position only, not enough to play next frame',
      3: 'HAVE_FUTURE_DATA — enough data for current position and at least the next frame',
      4: 'HAVE_ENOUGH_DATA — enough data for uninterrupted playback',
    }

    const networkStateLabels: Record<number, string> = {
      0: 'NETWORK_EMPTY — player not initialized',
      1: 'NETWORK_IDLE — resource selected, no network activity',
      2: 'NETWORK_LOADING — browser is downloading data',
      3: 'NETWORK_NO_SOURCE — no source found',
    }

    const p = video.player
    return {
      record_uuid: video.record_uuid,
      video_sources: video.video_sources,
      has_720p: video.has_720p,
      error_code: video.error_code,
      error_code_description: video.error_code != null ? this.getVideoJSErrorCodeLabel(video.error_code) : undefined,
      is_fallback_attempt: video.is_fallback_attempt,
      fallback_action: video.fallback_action,
      upload_state: video.upload_state,
      is_bot: video.is_bot,
      source_check: video.source_check,
      videojs_player: p
        ? {
            current_src: p.currentSrc,
            state: p.paused ? 'paused' : 'playing',
            current_time: p.currentTime,
            duration: p.duration,
            buffered_percent: p.bufferedPercent != null ? Math.round(p.bufferedPercent * 100) + '%' : undefined,
            ready_state: p.readyState,
            ready_state_description:
              p.readyState != null ? readyStateLabels[p.readyState] || `Unknown (${p.readyState})` : undefined,
            network_state: p.networkState,
            network_state_description:
              p.networkState != null ? networkStateLabels[p.networkState] || `Unknown (${p.networkState})` : undefined,
            video_width: p.videoWidth,
            video_height: p.videoHeight,
          }
        : undefined,
    }
  }
}
